import React, { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Button, Spin, Tag } from "antd";
import {
  BellOutlined,
  CheckCircleOutlined,
  ReloadOutlined,
  StopOutlined,
  ToolOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import {
  checkPushSubscriptionStatus,
  isPushNotificationSupported,
  subscribeToPushNotifications,
} from "../utils/webPushHelper";

export type WebPushUiStatus =
  | "checking"
  | "unsupported"
  | "not_enabled"
  | "blocked"
  | "needs_repair"
  | "active"
  | "error";

export function resolveWebPushUiStatus({
  supported,
  permission,
  isSubscribed,
}: {
  supported: boolean;
  permission: NotificationPermission;
  isSubscribed: boolean;
}): WebPushUiStatus {
  if (!supported) return "unsupported";
  if (permission === "denied") return "blocked";
  if (permission !== "granted") return "not_enabled";
  return isSubscribed ? "active" : "needs_repair";
}

const STATUS_CONTENT: Record<
  Exclude<WebPushUiStatus, "checking">,
  {
    alertType: "success" | "info" | "warning" | "error";
    description: React.ReactNode;
    icon: React.ReactNode;
    label: string;
    tagColor: string;
    title: string;
  }
> = {
  unsupported: {
    alertType: "warning",
    description:
      "Ce navigateur ou cet appareil ne prend pas en charge les notifications Web Push.",
    icon: <WarningOutlined />,
    label: "Non compatible",
    tagColor: "default",
    title: "Web Push indisponible",
  },
  not_enabled: {
    alertType: "info",
    description:
      "Recevez les alertes importantes même lorsque ELSA GESTION est fermée.",
    icon: <BellOutlined />,
    label: "Non activé",
    tagColor: "blue",
    title: "Activer les notifications sur cet appareil",
  },
  blocked: {
    alertType: "warning",
    description: (
      <>
        Les notifications ont été bloquées dans le navigateur. Ouvrez les
        paramètres du site depuis l’icône à gauche de l’adresse, passez
        <strong> Notifications</strong> sur <strong>Autoriser</strong>, puis
        revenez ici. Sur mobile ou dans la PWA, vérifiez également les réglages
        de notifications du système.
      </>
    ),
    icon: <StopOutlined />,
    label: "Bloqué",
    tagColor: "orange",
    title: "Notifications bloquées sur cet appareil",
  },
  needs_repair: {
    alertType: "warning",
    description:
      "L’autorisation est accordée, mais l’abonnement de cet appareil n’est plus actif.",
    icon: <ToolOutlined />,
    label: "À réparer",
    tagColor: "gold",
    title: "Abonnement Web Push inactif",
  },
  active: {
    alertType: "success",
    description:
      "Cet appareil peut recevoir les alertes ELSA GESTION en arrière-plan.",
    icon: <CheckCircleOutlined />,
    label: "Actif",
    tagColor: "green",
    title: "Notifications Web Push actives",
  },
  error: {
    alertType: "error",
    description:
      "ELSA GESTION n’a pas pu vérifier l’abonnement de cet appareil. Réessayez lorsque la connexion est disponible.",
    icon: <WarningOutlined />,
    label: "Erreur",
    tagColor: "red",
    title: "Vérification impossible",
  },
};

const WebPushSettings: React.FC = () => {
  const [status, setStatus] = useState<WebPushUiStatus>("checking");
  const [working, setWorking] = useState(false);
  const requestIdRef = useRef(0);

  const refreshStatus = useCallback(async (repairIfGranted = false) => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setWorking(true);

    try {
      const supported = isPushNotificationSupported();
      if (!supported) {
        if (requestIdRef.current === requestId) setStatus("unsupported");
        return;
      }

      let snapshot = await checkPushSubscriptionStatus();

      let synchronizationSucceeded = true;
      if (repairIfGranted && snapshot.permission === "granted") {
        synchronizationSucceeded = await subscribeToPushNotifications({
          requestPermission: false,
        });
        snapshot = await checkPushSubscriptionStatus();
      }

      if (requestIdRef.current === requestId) {
        const nextStatus = resolveWebPushUiStatus({
          supported,
          permission: snapshot.permission,
          isSubscribed: snapshot.isSubscribed,
        });
        setStatus(
          !synchronizationSucceeded && nextStatus === "active"
            ? "needs_repair"
            : nextStatus
        );
      }
    } catch (_error) {
      if (requestIdRef.current === requestId) setStatus("error");
    } finally {
      if (requestIdRef.current === requestId) setWorking(false);
    }
  }, []);

  useEffect(() => {
    void refreshStatus(true);

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        void refreshStatus(true);
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      requestIdRef.current += 1;
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [refreshStatus]);

  const activateNotifications = async () => {
    setWorking(true);
    try {
      await subscribeToPushNotifications({ requestPermission: true });
      await refreshStatus(true);
    } catch (_error) {
      setStatus("error");
    } finally {
      setWorking(false);
    }
  };

  const content = status === "checking" ? null : STATUS_CONTENT[status];

  const action = (() => {
    switch (status) {
      case "not_enabled":
        return (
          <Button
            type="primary"
            icon={<BellOutlined />}
            loading={working}
            onClick={activateNotifications}
          >
            Activer les notifications
          </Button>
        );
      case "blocked":
        return (
          <Button
            icon={<ReloadOutlined />}
            loading={working}
            onClick={() => void refreshStatus(true)}
          >
            Vérifier à nouveau
          </Button>
        );
      case "needs_repair":
      case "error":
        return (
          <Button
            type="primary"
            icon={<ToolOutlined />}
            loading={working}
            onClick={() => void refreshStatus(true)}
          >
            Réparer l’abonnement
          </Button>
        );
      case "active":
        return (
          <Button
            icon={<ReloadOutlined />}
            loading={working}
            onClick={() => void refreshStatus(true)}
          >
            Vérifier maintenant
          </Button>
        );
      default:
        return null;
    }
  })();

  return (
    <div className="user-profile-modal__push-panel" aria-live="polite">
      <div className="user-profile-modal__push-heading">
        <div>
          <h3>Notifications Web Push</h3>
          <p>État de la réception sur cet appareil et ce navigateur.</p>
        </div>
        {content ? <Tag color={content.tagColor}>{content.label}</Tag> : null}
      </div>

      <Spin spinning={status === "checking" || working} tip="Vérification...">
        {content ? (
          <Alert
            type={content.alertType}
            showIcon
            icon={content.icon}
            message={content.title}
            description={content.description}
          />
        ) : (
          <div className="user-profile-modal__push-placeholder" />
        )}

        {action ? (
          <div className="user-profile-modal__push-actions">{action}</div>
        ) : null}
      </Spin>

      <div className="user-profile-modal__push-note">
        Le Web Push est propre à chaque appareil. Le bloquer ne désactive pas
        les notifications disponibles dans la cloche et l’Agenda ELSA GESTION.
      </div>
    </div>
  );
};

export default WebPushSettings;
